import { useEffect, useRef, useState } from 'react';

/**
 * Load-state messages for whatever hosts a widget: the parent page when the widget is in an
 * iframe, and the native app when it is loaded straight into a WKWebView. Partners build
 * against the message shape and the handler name, so add fields but never rename or remove them.
 *
 * - `{ type: 'kalshi-widget-ready', eventTicker }` once the event chart has drawn.
 * - `{ type: 'kalshi-widget-error', reason, eventTicker? }` when there is no chart to show.
 *   `reason` is informational and may gain values:
 *   - `unavailable`: the widget could not be rendered (unknown ticker, server error).
 *   - `load_failed`: the event loaded on the server but not in the browser.
 *   - `no_chart`: the event loaded, but has too little trading or history to chart, so the widget
 *     shows its fallback image instead.
 *
 * An iOS app receives them by registering a script message handler named `kalshiWidget`:
 * `webView.configuration.userContentController.add(handler, name: "kalshiWidget")`.
 */

export const HOST_MESSAGE_HANDLER = 'kalshiWidget';

export type HostErrorReason = 'unavailable' | 'load_failed' | 'no_chart';

export type HostMessage =
    | { type: 'kalshi-widget-ready'; eventTicker: string }
    | { type: 'kalshi-widget-error'; reason: HostErrorReason; eventTicker?: string };

type WebKitWindow = Window & {
    webkit?: { messageHandlers?: Record<string, { postMessage: (message: unknown) => void } | undefined> };
};

export function postToHost(message: HostMessage): void {
    if (typeof window === 'undefined') return;
    if (window.parent !== window) {
        // Nothing private here (public tickers only), so any embedding origin may read it.
        window.parent.postMessage(message, '*');
    }
    (window as WebKitWindow).webkit?.messageHandlers?.[HOST_MESSAGE_HANDLER]?.postMessage(message);
}

/**
 * Posts `ready` the first time `loaded` is true and `error` the first time there is a `failure`,
 * with ready allowed after an error (a later poll can still land) but never an error after ready.
 */
export function useHostLoadSignal(
    eventTicker: string,
    { loaded, failure }: { loaded: boolean; failure: HostErrorReason | null },
) {
    const notifiedRef = useRef<'ready' | 'error' | null>(null);
    useEffect(() => {
        if (loaded && notifiedRef.current !== 'ready') {
            notifiedRef.current = 'ready';
            postToHost({ type: 'kalshi-widget-ready', eventTicker });
        } else if (failure && notifiedRef.current === null) {
            notifiedRef.current = 'error';
            postToHost({ type: 'kalshi-widget-error', reason: failure, eventTicker });
        }
    }, [loaded, failure, eventTicker]);
}

/**
 * True once `value` has stayed true for `ms`; false again as soon as it is not. For a verdict that
 * can be one render stale, such as a chart that takes in new history a commit after it arrives.
 */
export function useHeldFor(value: boolean, ms: number): boolean {
    const [held, setHeld] = useState(false);
    // Reset when the value changes, during render rather than in an effect, so a value that comes
    // back true starts a fresh window.
    const [previousValue, setPreviousValue] = useState(value);
    if (value !== previousValue) {
        setPreviousValue(value);
        setHeld(false);
    }
    useEffect(() => {
        if (!value) return;
        const timer = setTimeout(() => setHeld(true), ms);
        return () => clearTimeout(timer);
    }, [value, ms]);
    return value && held;
}
